import { useSelector } from 'react-redux';
import { useGetAccountQuery, useGetTenantAccountQuery } from '../api/account/accountApiSlice';
import { tenantSelector } from '../selector/authenticationSelector';
import { useIsMaster } from '../common/useIsMaster';

/**
 * Is kolula the invoice issuer for the tenant currently being looked at?
 * (ADR-0012 Mode A, #1637)
 *
 * **The displayed tenant, not the caller's own.** kolula staff operate
 * production as MASTERS with no tenant of their own: `/admin/account` answers
 * `onboardingPending` for them, so an own-account read returns false for every
 * tenant and the whole feature is invisible exactly where it is used. Found by
 * opening the real production admin — every test and every integration run had
 * an owner, so nothing caught it.
 *
 * One definition across the four screens that ask: two booking-detail screens
 * hide the client-side PDF button on it, two edit screens hide the free-text
 * number field. Four inline copies of this question is the shape that drifted
 * in #1875.
 *
 * **The MODE alone decides, not the mode plus a number circle** (PO,
 * 2026-09-29; ADR-0012). A Mode A tenant without a circle sees neither the
 * free-text field nor the old client-side PDF — only the readiness message in
 * the invoice section pointing at Konto → Rechnungsnummernkreis. No scheme, no
 * invoicing: the old PDF was a document kolula produced with none of the
 * machinery (nothing stored, no gap-free number, no correction path), and
 * ADR-0012 forbids that as a standing option. This replaces
 * `useInvoiceIssuanceActive` (mode AND circle), which kept exactly that
 * in-between state open; the PO accepted that a Mode A tenant cannot invoice
 * until its circle is seeded, and asked for no bridge.
 *
 * **Mode C is not a lesser case.** A tenant whose own ERP owns the invoice goes
 * on typing its number into the free-text field, and that stays correct for
 * them indefinitely. kolula's client-side PDF is NOT offered to them: their
 * system issues the document (`mayIssueInvoiceFromKolula`, aa47296de).
 */
export const useIsInvoiceIssuingTenant = (): boolean => {
    const isMaster = useIsMaster();
    const displayed = useSelector(tenantSelector);
    const slug = displayed ?? '';

    const own = useGetAccountQuery(undefined, { skip: isMaster });
    const viaMaster = useGetTenantAccountQuery(slug, { skip: !isMaster || !slug });

    if (isMaster) return viaMaster.data?.tenant?.invoiceIssuer === 'kolula';

    // GetAccountResponse is a union — an onboarding-pending tenant carries no
    // `tenant` at all, so this narrows rather than optional-chains.
    const data = own.data;
    const tenant = data && 'tenant' in data ? data.tenant : null;
    return tenant?.invoiceIssuer === 'kolula';
};

/** The slug to address when acting as a master, or undefined when acting as the owner. */
export const useInvoiceMasterSlug = (): string | undefined => {
    const isMaster = useIsMaster();
    const displayed = useSelector(tenantSelector);
    return isMaster ? (displayed || undefined) : undefined;
};
